// AVRASTRO Video (requirements 01–02) — the CRM side, pure. No data-schema change: the room lives on the platform; the
// appointment (tasks, kind 'appointment') only remembers its room id in its free `data` field, and the meeting record of
// a video consultation is the ordinary `meetings` row of the appointment's client (taskId = the appointment).
//
// «Shared View»: during a call the advisor shows the client a CLIENT-SAFE picture of the current Meeting Mode step. The
// payload is built here from an allow-list (what Meeting Mode already shows on the client's side of the table: the
// household, incomes/loans/goals amounts, policies in force with product, insurer and premium, protection status,
// discussion topics, agreed decisions). It never carries commission, private notes (the person's notes, the advisor's
// meeting notes), tasks, other clients or anything else of the CRM. `cleanSharedView` is the same allow-list as a
// sanitiser: the platform runs it again on what it relays, so a wrong client cannot widen it.
import { ageOn, type Day } from './dates.ts';
import { DomainError, touched, put, type Ctx } from './cmd.ts';
import type { Changeset, WorkspaceView } from './changeset.ts';
import { annualOf, discussionTopics, familyNeeds, familyNeedsInputs, householdMap, householdOf, PROTECTION_AREAS } from './household.ts';
import { premiumView } from './policy-engine.ts';
import { isActivePolicy } from './rules.ts';
import type { Task } from './schema.ts';

export const SHARED_STEPS = ['family', 'income', 'loans', 'insurance', 'goals', 'analysis', 'discussion', 'summary'] as const;
export type SharedStep = typeof SHARED_STEPS[number];
export type SharedStatus = 'protected' | 'partial' | 'gap' | 'not_evaluated' | 'not_needed';
export type SharedBlock =
  | { t: 'h'; text: string }
  | { t: 'p'; text: string }
  | { t: 'kv'; label: string; value: string }
  | { t: 'status'; label: string; status: SharedStatus; text: string }
  | { t: 'list'; items: string[] };
export interface SharedView { v: 1; step: SharedStep; title: string; blocks: SharedBlock[]; note: string | null }

const LIMITS = { title: 120, text: 300, label: 120, value: 160, items: 20, blocks: 40, note: 300, bytes: 16 * 1024 };
const STATUSES: readonly SharedStatus[] = ['protected', 'partial', 'gap', 'not_evaluated', 'not_needed'];
const str = (s: unknown, max: number) => String(s ?? '').replace(/[\u0000-\u001f\u007f‪-‮⁦-⁩]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, max);

/** the allow-list as a sanitiser: unknown blocks and fields are dropped, strings capped; null when nothing valid remains */
export function cleanSharedView(input: unknown): SharedView | null {
  if (!input || typeof input !== 'object') return null;
  const o = input as Record<string, unknown>;
  const step = (SHARED_STEPS as readonly string[]).includes(String(o.step)) ? o.step as SharedStep : null;
  if (!step || !Array.isArray(o.blocks)) return null;
  const blocks: SharedBlock[] = [];
  for (const b of o.blocks.slice(0, LIMITS.blocks)) {
    if (!b || typeof b !== 'object') continue;
    const x = b as Record<string, unknown>;
    if ((x.t === 'h' || x.t === 'p') && str(x.text, LIMITS.text)) blocks.push({ t: x.t, text: str(x.text, LIMITS.text) });
    else if (x.t === 'kv' && str(x.label, LIMITS.label)) blocks.push({ t: 'kv', label: str(x.label, LIMITS.label), value: str(x.value, LIMITS.value) });
    else if (x.t === 'status' && str(x.label, LIMITS.label) && STATUSES.includes(x.status as SharedStatus)) blocks.push({ t: 'status', label: str(x.label, LIMITS.label), status: x.status as SharedStatus, text: str(x.text, LIMITS.label) });
    else if (x.t === 'list' && Array.isArray(x.items)) { const items = x.items.slice(0, LIMITS.items).map((i) => str(i, LIMITS.value)).filter(Boolean); if (items.length) blocks.push({ t: 'list', items }); }
  }
  const out: SharedView = { v: 1, step, title: str(o.title, LIMITS.title), blocks, note: str(o.note, LIMITS.note) || null };
  while (JSON.stringify(out).length > LIMITS.bytes && out.blocks.length) out.blocks.pop();
  return out;
}

export interface SharedViewText {
  t: (key: string, params?: Record<string, string | number>) => string;
  money: (n: number, currency?: string) => string;
  enumLabel: (enumName: string, value: string) => string;
  date: (d: string | null | undefined) => string;
}
/**
 * The client-safe picture of one Meeting Mode step for the client `personId` (and only their household). `decisions` are
 * the topics the advisor ticked in the discussion (shown on the summary). The advisor's free-text notes are NEVER shown.
 */
export function sharedMeetingView(v: WorkspaceView, personId: string, step: SharedStep, today: Day, tx: SharedViewText, o: { decisions?: string[]; region?: string | null } = {}): SharedView {
  const p = v.get('persons', personId);
  if (!p) throw new DomainError('person.not_found');
  const { t } = tx;
  const blocks: SharedBlock[] = [];
  const members = householdOf(v, personId);
  const memberIds = new Set(members.map((m) => m.person.id));
  const items = v.where('financial_items', 'personId', personId);
  if (step === 'family') {
    for (const m of members) blocks.push({ t: 'kv', label: m.person.displayName, value: [tx.enumLabel('householdRole', m.role), m.person.birthDate ? t('meeting_mode.age', { age: ageOn(m.person.birthDate, today) }) : ''].filter(Boolean).join(' · ') });
  } else if (step === 'income') {
    for (const x of items.filter((i) => i.kind === 'income' || i.kind === 'expense')) {
      const a = annualOf(x);
      blocks.push({ t: 'kv', label: `${t(`finances.kind.${x.kind}`)} · ${x.label ?? t(`finances.type.${x.type}`)}`, value: a != null ? `${tx.money(a, x.currency ?? undefined)} / ${t('finances.year')}` : t('finances.unknown') });
    }
  } else if (step === 'loans') {
    for (const x of items.filter((i) => i.kind === 'liability')) blocks.push({ t: 'kv', label: `${t(`finances.type.${x.type}`)}${x.lender ? ` · ${x.lender}` : ''}`, value: x.balance != null ? tx.money(x.balance, x.currency ?? undefined) : t('finances.unknown') });
  } else if (step === 'insurance') {
    // policies in force of THIS household only; product, insurer, premium and sum assured — never commission
    for (const x of v.rows('policies').filter((pol) => isActivePolicy(pol) && pol.personId != null && memberIds.has(pol.personId))) {
      const pv = premiumView(x, v.where('policy_adjustments', 'policyId', x.id), today, o.region ?? null);
      const premium = pv.recurring ? `${tx.money(pv.monthlyEquivalent ?? 0, pv.currency)} / ${tx.enumLabel('frequency', 'monthly').toLowerCase()}` : tx.money(pv.original, pv.currency);
      blocks.push({ t: 'kv', label: [x.productName, x.carrier].filter(Boolean).join(' · '), value: [premium, x.sumAssured ? `${t('policy.sum_assured')} ${tx.money(x.sumAssured, pv.currency)}` : ''].filter(Boolean).join(' · ') });
    }
  } else if (step === 'goals') {
    for (const x of items.filter((i) => i.kind === 'goal')) blocks.push({ t: 'kv', label: `${t(`finances.type.${x.type}`)}${x.endDate ? ` · ${tx.date(x.endDate)}` : ''}`, value: x.balance != null ? tx.money(x.balance) : '—' });
  } else if (step === 'analysis') {
    const map = householdMap(v, personId, today);
    for (const a of PROTECTION_AREAS) { const f = map.family.find((x) => x.area === a)!; blocks.push({ t: 'status', label: t(`protection.area.${a}`), status: f.status as SharedStatus, text: t(`protection.status.${f.status}`) }); }
    const fn = familyNeeds(familyNeedsInputs(v, personId, today));
    blocks.push(fn.status === 'evaluated' ? { t: 'kv', label: t('fneeds.capital'), value: tx.money(fn.capitalNeed ?? 0) } : { t: 'p', text: t('meeting_mode.not_evaluated') });
  } else if (step === 'discussion') {
    const topics = discussionTopics(v, personId, today).map((x) => t(`protection.topic.${x.key}`));
    if (topics.length) blocks.push({ t: 'list', items: topics });
  } else if (step === 'summary') {
    const d = (o.decisions ?? []).map((x) => String(x)).filter(Boolean);
    if (d.length) blocks.push({ t: 'list', items: d });
  }
  const note = step === 'analysis' || step === 'summary' ? t('meeting_mode.disclaimer') : null;
  return cleanSharedView({ v: 1, step, title: `${t(`meeting_mode.step.${step}`)} · ${p.displayName}`, blocks, note })!;
}

// --- the appointment ↔ room link (task.data.video) ---------------------------------------------------------------
export interface TaskVideo { roomId: string; mode?: 'standard' | 'confidential' }
export function videoOfTask(t: Pick<Task, 'data'> | null | undefined): TaskVideo | null {
  const d = (t?.data ?? null) as { video?: { roomId?: unknown; mode?: unknown } } | null;
  const id = d?.video?.roomId;
  return typeof id === 'string' && /^[a-z0-9]{20,40}$/.test(id) ? { roomId: id, mode: d!.video!.mode === 'confidential' ? 'confidential' : 'standard' } : null;
}
/** remember (or forget, with null) the platform room of an appointment; the place becomes «AVRASTRO Video» (the guest link itself stays on the platform) */
export function linkVideoRoom(v: WorkspaceView, taskId: string, room: TaskVideo | null, ctx: Ctx, label = 'AVRASTRO Video'): Changeset {
  const task = v.get('tasks', taskId);
  if (!task) throw new DomainError('task.not_found');
  if (task.kind !== 'appointment') throw new DomainError('video.not_an_appointment');
  const data = { ...((task.data ?? {}) as Record<string, unknown>) };
  if (room) data.video = { roomId: room.roomId, mode: room.mode ?? 'standard' }; else delete data.video;
  const patch: Partial<Task> = { data };
  if (room) { patch.locationType = 'online'; patch.location = label; }
  return { action: 'task.video', entity: 'tasks', entityId: taskId, summary: task.title, puts: [put('tasks', touched(ctx, task, patch))], deletes: [] };
}
